import React, { useEffect, useState, memo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  MenuItem,
  Select,
  Checkbox,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
} from "@mui/material";
import style from "../../styles/gri/general.module.css";
import { getRowErrorMessages } from "../../utils/getRowErrorMessage";
import {
  validateNumberInput,
  validatePercentageInput,
  validateRatio,
} from "../../utils/validateFields";

const StaticColumnTable = memo(
  ({
    inputData,
    subHeading,
    tables,
    setTables,
    errors,
    totalField,
    setErrors,
    data,
    defaultSegments,
    defaultSegments2,
    parameter,
    parameter2,
    rowTotalField,
    fieldName,
    disabled,
    hide
  }) => {
    const [total, setTotal] = useState({});
    const [rowTotal, setRowTotal] = useState({});
    useEffect(() => {
      setTables(
        inputData?.map((section) => ({
          fieldName: section?.fieldName,
          rows:
            data?.[section?.fieldName]?.length > 0
              ? data[section?.fieldName]
              : defaultSegments?.map((el, i) => ({
                  [parameter]: el,
                  ...(parameter2
                    ? { [parameter2]: defaultSegments2?.[i] }
                    : {}),
                })),
        }))
      );
    }, [data]);

    useEffect(() => {
      if (!tables?.length) return; // Ensure tables exist before calculation

      rowTotalField &&
        setRowTotal((prev) => {
          const totals = tables?.reduce((acc, section, tableIndex) => {
            const rowTotals = section?.rows?.map((row) => {
              const values = Object.values(row).slice(1, -1); // Exclude first & last column
              const total = row?.[rowTotalField]
                ? row?.[rowTotalField]
                : values.reduce((sum, val) => {
                    const num = Number(val);
                    return sum + (isNaN(num) ? 0 : num);
                  }, 0);
              return total;
            });

            return { ...acc, [tableIndex]: rowTotals }; // Store totals per row
          }, {});

          console.log("Updated Row Totals:", totals, "v1");
          return { ...prev, ...totals };
        });
      if (fieldName && fieldName === "odsProduction") calculateTotal();
    }, [tables]); // Run after `tables` updates

    const calculateTotal = (tableIndex = 0) => {
      setRowTotal((prev) => {
        const rowTotals = tables?.[tableIndex]?.rows?.map((row) => {
          const values = Object.values(row);
    
          const firstVal = Number(values[1]); // First column
          const secondVal = Number(values[2]); // Second column
          const thirdVal = Number(values[3]); // Third column
    
          // Ensure all values are valid numbers, default to 0 if NaN
          const total =
            (isNaN(firstVal) ? 0 : firstVal) -
            ((isNaN(secondVal) ? 0 : secondVal) + (isNaN(thirdVal) ? 0 : thirdVal));
    
          return total;
        });
    
        return { ...prev, [tableIndex]: rowTotals };
      });
    };
    

    const calculate = (tableIndex, fieldName) => {
      const total = tables[tableIndex].rows?.reduce((acc, el) => {
        return Number(acc) + Number(el?.[fieldName] || 0); // Sum up the field values
      }, 0);

      setTotal((prev) => ({ ...prev, [tableIndex]: total }));
    };

    const handleInputChange = (tableIndex, rowIndex, field, value, type) => {
      console.log(value);
      if (type === "percentage" && !validatePercentageInput(value)) return;
      if (type === "number" && !validateNumberInput(value)) return;
      if (type === "ratio" && !validateRatio(value)) return;

      const updatedTables = [...tables];
      if (fieldName === "odsProduction" && field!=="odsProduction") {
        console.log(
          "ods1",field,
          Number(updatedTables[tableIndex].rows[rowIndex]?.odsProduction),
          Number(
            field === "odsEntirelyUsed"
              ? updatedTables[tableIndex].rows[rowIndex]?.odsDestroyed || 0
              :  updatedTables[tableIndex].rows[rowIndex]?.odsEntirelyUsed || 0
          ) + Number(value)
        );
        const total =
          Number(
            field === "odsEntirelyUsed"
              ? updatedTables[tableIndex].rows[rowIndex]?.odsDestroyed || 0
              : updatedTables[tableIndex].rows[rowIndex]?.odsEntirelyUsed || 0
          ) + Number(value);
        if (
          Number(updatedTables[tableIndex].rows[rowIndex]?.odsProduction) <
          total
        )
          return;
      }
      updatedTables[tableIndex].rows[rowIndex][field] = value;

      const updatedErrors = { ...errors };
      if (updatedErrors[tableIndex] && updatedErrors[tableIndex][rowIndex]) {
        delete updatedErrors[tableIndex][rowIndex][field];
        if (Object.keys(updatedErrors[tableIndex][rowIndex]).length === 0) {
          delete updatedErrors[tableIndex][rowIndex];
        }
        if (Object.keys(updatedErrors[tableIndex]).length === 0) {
          delete updatedErrors[tableIndex];
        }
      }
      setErrors(updatedErrors);
      setTables(updatedTables);
      if (field === totalField) calculate(tableIndex, field);
      if (rowTotalField) calculateTotal(tableIndex);
    };

    return (
      <Box>
        {subHeading && <p className={style.subHeading}>{subHeading}</p>}
        {inputData?.map((section, tableIndex) => (
          <div key={section.fieldName} sx={{ marginBottom: "1rem" }}>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {section?.letter && (
                <p className={style.ques_heading}>{section.letter}</p>
              )}
              <p className={style.ques_heading}>{section.title}</p>
            </Box>

            <TableContainer
              component={Paper}
              sx={{
                boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                borderRadius: "10px",
                ml: 2,
                width: "98.5%",
              }}
            >
              <Table sx={{ border: "1px solid #ddd", borderRadius: "10px" }}>
                <TableHead>
                  <TableRow
                    sx={{
                      borderRadius: "10px",
                      backgroundColor: "#1C486B",
                      border: "1px solid #ddd",
                    }}
                  >
                    <TableCell
                      sx={{
                        border: "1px solid #ddd",
                        fontWeight: "bold",
                        color: "#FFFFFF",
                        width: "1rem",
                        textAlign: "center",
                      }}
                    >
                      S. No.
                    </TableCell>
                    {section?.fields?.map((field) => (
                      <TableCell
                        key={field?.fieldName}
                        style={{
                          border: "1px solid #ddd",
                          fontWeight: "bold",
                          color: "#FFFFFF",
                          width: field.width,
                        }}
                      >
                        {field?.title}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(totalField
                    ? [...(tables[tableIndex]?.rows || []), "Total"]
                    : tables[tableIndex]?.rows || []
                  ).map((row, rowIndex) => {
                    console.log(row, "checking row");
                    return (
                      <TableRow
                        key={rowIndex}
                        sx={{
                          backgroundColor: row === "Total" && "#DBDBDB",
                          "& > th": {
                            color: "white",
                            fontWeight: "bold",
                            py: "0.5rem",
                            px: "0.5rem",
                            border: "1px solid rgba(224, 224, 224, 1)",
                            height: "auto",
                          },
                        }}
                      >
                        <TableCell sx={{ textAlign: "center", width: "8%" }}>
                          {`${row === "Total" ? "" : rowIndex + 1 + "."}`}
                        </TableCell>
                        {section?.fields?.map((field, i) => (
                          <TableCell
                            key={field?.fieldName}
                            sx={{
                              backgroundColor:
                                rowTotalField === field?.fieldName && "#DBDBDB",
                              border: "1px solid #DBDBDB",
                              py: "0.5rem",
                              maxWidth: field?.width,
                              minWidth: field.width,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              fontWeight:
                                (field.fieldName === parameter ||
                                  field.fieldName === parameter2) &&
                                400,
                              color:
                                (field.fieldName === parameter ||
                                  field.fieldName === parameter2) &&
                                "#1C486B",
                            }}
                          >
                            {field.fieldName === parameter ||
                            field.fieldName === parameter2 ? (
                              <p
                                style={{
                                  fontWeight: 400,
                                  fontSize: "14px",
                                  lineHeight: "16.94px",
                                  fontFamily: "inter",
                                  marginTop: "1rem",
                                }}
                              >
                                {row[field.fieldName]}
                              </p> // Display the value as plain text if the condition is true
                            ) : row === "Total" ? (
                              totalField === field?.fieldName && (
                                <p
                                  style={{
                                    fontWeight: 400,
                                    fontSize: "14px",
                                    lineHeight: "16.94px",
                                    fontFamily: "inter",
                                    marginTop: "1rem",
                                  }}
                                >
                                  {total?.[tableIndex] || 0}
                                </p>
                              )
                            ) : field?.type === "select" ? (
                              <Select
                                disabled={
                                  disabled || 
                                  (rowIndex < defaultSegments?.length &&
                                  field.fieldName === parameter)
                                }
                                multiple={field?.multiselect}
                                multiline
                                minRows={1}
                                width={field?.width}
                                value={
                                  field?.multiselect
                                    ? row[field?.fieldName] || [] // Ensure value is an array for multiselect
                                    : row[field?.fieldName] || "" // Ensure value is a string for single select
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    tableIndex,
                                    rowIndex,
                                    field.fieldName,
                                    e.target.value,
                                    field.type
                                  )
                                }
                                displayEmpty
                                fullWidth
                                size="small"
                                sx={{
                                  border: "none",
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                  },
                                  "&:hover .MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                  },
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                      border: "none",
                                    },
                                }}
                                renderValue={(selected) =>
                                  field.multiselect
                                    ? selected.length > 0
                                      ? selected.join(", ")
                                      : field.placeholder || "Select"
                                    : selected || field.placeholder || "Select"
                                }
                              >
                                <MenuItem value={""} disabled>
                                  {field.placeholder || "Select"}
                                </MenuItem>
                                {field.options?.map((option, idx) => (
                                  <MenuItem
                                    key={idx}
                                    value={option?.name || option} // Ensure the `value` matches the `row[field.fieldName]`
                                  >
                                    {field.multiselect ? (
                                      <>
                                        <Checkbox
                                        disabled={disabled}
                                          checked={
                                            row[field.fieldName]?.includes(
                                              option?.name || option
                                            ) || false
                                          } // Check if the option is selected
                                          size="small"
                                        />
                                        {option?.name || option}
                                      </>
                                    ) : (
                                      option?.name || option
                                    )}
                                  </MenuItem>
                                ))}
                              </Select>
                            ) : field.type === "radio" ? (
                              // console.log(row,row[field.fieldName],"Checking...")
                              <Box sx={{ display: "flex" }}>
                                <FormControl sx={{ my: 1, display: "flex" }}>
                                  <RadioGroup
                                    row
                                    disabled={
                                      disabled || 
                                      (rowIndex <= defaultSegments?.length &&
                                      field.fieldName === parameter)
                                    }
                                    value={row[field.fieldName] || "Yes"}
                                    onChange={(e) =>
                                      handleInputChange(
                                        tableIndex,
                                        rowIndex,
                                        field.fieldName,
                                        e.target.value,
                                        field.type
                                      )
                                    }
                                  >
                                    <FormControlLabel
                                      value="Yes"
                                      control={<Radio />}
                                      label="Yes"
                                    />
                                    <FormControlLabel
                                      value="No"
                                      control={<Radio />}
                                      label="No"
                                    />
                                  </RadioGroup>
                                </FormControl>
                              </Box>
                            ) : (
                              <TextField
                                multiline
                                minRows={1}
                                disabled={
                                  disabled || 
                                  (rowIndex < defaultSegments?.length &&
                                    field.fieldName === parameter) ||
                                  rowTotalField === field?.fieldName
                                }
                                placeholder={field.placeholder}
                                type={field.type}
                                value={
                                  row === "Total"
                                    ? "Total"
                                    : rowTotalField === field?.fieldName
                                    ? rowTotal?.[tableIndex]?.[rowIndex]
                                    : row[field.fieldName] || ""
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    tableIndex,
                                    rowIndex,
                                    field.fieldName,
                                    e.target.value,
                                    field.type
                                  )
                                }
                                sx={{
                                  color: "#1C486B",
                                  width: "100%",
                                  py: "0.5rem",
                                  "& .MuiInputBase-root": {
                                    overflowY: "auto",
                                    cursor: "default",
                                  },
                                  "& .MuiInputBase-root:hover": {
                                    cursor: "pointer",
                                  },
                                }}
                                size="small"
                                variant="standard"
                                InputProps={{
                                  disableUnderline: true,
                                  sx: {
                                    color:
                                      field.fieldName === parameter &&
                                      "#1C486B",
                                    "&.Mui-disabled": {
                                      color: "red", // Ensure font color change works for disabled state
                                    },
                                  },
                                }}
                              />
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ mt: 1, ml: 3 }}>
              {Object.keys(errors).length > 0 &&
                getRowErrorMessages(tableIndex, inputData, tables, errors).map(
                  (errorMessage, idx) => (
                    <p
                      key={idx}
                      color="error"
                      variant="caption"
                      style={{
                        lineHeight: "1.2",
                        margin: "2px 0",
                        color: "#d34040",
                        fontSize: "10px",
                        fontStyle: "italic",
                      }}
                    >
                      {errorMessage}
                    </p>
                  )
                )}
            </Box>
          </div>
        ))}
      </Box>
    );
  }
);

export default StaticColumnTable;
